import { useState, useEffect, useRef } from "react";
import EnvironmentalStatusBadge from "./EnvironmentalStatusBadge";
import { arvoresAreaVerde, documentosUC, carbonoEstimativas, historicoUC } from "@/mocks/ambiental";

interface EnvironmentalDetailDrawerProps {
  open: boolean;
  item: any | null;
  isUC: boolean;
  onClose: () => void;
}

export default function EnvironmentalDetailDrawer({ open, item, isUC, onClose }: EnvironmentalDetailDrawerProps) {
  const [activeTab, setActiveTab] = useState("overview");
  const overlayRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (open) setActiveTab("overview");
  }, [open]);

  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    if (open) document.addEventListener("keydown", handleEsc);
    return () => document.removeEventListener("keydown", handleEsc);
  }, [open, onClose]);

  if (!open || !item) return null;

  const tabs = [
    { key: "overview", label: "Visão Geral", icon: "ri-dashboard-line" },
    { key: "trees", label: "Árvores do CTA", icon: "ri-plant-line" },
    { key: "map", label: "Mapa", icon: "ri-map-pin-line" },
    { key: "inspections", label: "Vistorias", icon: "ri-search-eye-line" },
    { key: "documents", label: "Documentos", icon: "ri-file-text-line" },
    { key: "carbon", label: "Carbono", icon: "ri-cloud-line" },
    { key: "history", label: "Histórico", icon: "ri-history-line" },
  ];

  const carbono = carbonoEstimativas.find((c) => c.areaId === item.id) || carbonoEstimativas[0];

  return (
    <>
      {/* Overlay */}
      <div ref={overlayRef} className="fixed inset-0 bg-black/20 z-40" onClick={onClose}></div>

      {/* Drawer */}
      <div className="fixed inset-y-0 right-0 w-full max-w-xl bg-white shadow-xl z-50 flex flex-col animate-slide-in-right" style={{ animation: "slideInRight 0.25s ease-out" }}>
        {/* Header */}
        <div className="shrink-0 px-6 py-4 border-b border-background-200">
          <div className="flex items-start justify-between">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <h3 className="text-lg font-semibold text-foreground-900 font-heading truncate">{item.nome}</h3>
                <EnvironmentalStatusBadge status={isUC ? item.statusGestao : item.status} />
              </div>
              <p className="text-sm text-foreground-500">
                {isUC ? `${item.categoria} · ${item.grupo}` : item.tipo}
                {" · "}{item.municipio}/{item.uf} · {item.areaHa} ha
              </p>
            </div>
            <button onClick={onClose} className="w-8 h-8 rounded-btn flex items-center justify-center text-foreground-400 hover:bg-background-100 hover:text-foreground-700 transition-colors cursor-pointer ml-2 shrink-0">
              <i className="ri-close-line text-lg"></i>
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="shrink-0 px-6 pt-3 border-b border-background-200">
          <div className="flex gap-1 overflow-x-auto pb-2">
            {tabs.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-full transition-colors whitespace-nowrap cursor-pointer ${
                  activeTab === tab.key
                    ? "bg-primary-50 text-primary-700"
                    : "text-foreground-500 hover:bg-background-100 hover:text-foreground-700"
                }`}
              >
                <i className={`${tab.icon} text-xs`}></i>
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {/* Visão Geral */}
          {activeTab === "overview" && (
            <div className="space-y-4">
              {/* Summary grid */}
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-background-50 rounded-xl p-4">
                  <p className="text-xs text-foreground-500 mb-1">Área Total</p>
                  <p className="text-2xl font-bold text-foreground-900">{item.areaHa} <span className="text-sm font-normal text-foreground-500">ha</span></p>
                </div>
                <div className="bg-background-50 rounded-xl p-4">
                  <p className="text-xs text-foreground-500 mb-1">{isUC ? "Árvores Cadastradas" : "Árvores Cadastradas"}</p>
                  <p className="text-2xl font-bold text-foreground-900">{item.arvoresCadastradas.toLocaleString("pt-BR")}</p>
                </div>
                <div className="bg-background-50 rounded-xl p-4">
                  <p className="text-xs text-foreground-500 mb-1">{isUC ? "Áreas Degradadas" : "Risco Crítico"}</p>
                  <p className={`text-2xl font-bold ${isUC ? (item.areasDegradadas > 10 ? "text-danger-600" : "text-foreground-900") : (item.arvoresRiscoCritico > 0 ? "text-danger-600" : "text-foreground-900")}`}>
                    {isUC ? `${item.areasDegradadas} ha` : item.arvoresRiscoCritico}
                  </p>
                </div>
                <div className="bg-background-50 rounded-xl p-4">
                  <p className="text-xs text-foreground-500 mb-1">CO₂e Estimado</p>
                  <p className="text-2xl font-bold text-foreground-900">{item.co2eEstimado} <span className="text-sm font-normal text-foreground-500">t</span></p>
                </div>
              </div>

              {/* General info */}
              <div className="bg-white rounded-xl border border-background-200 p-4">
                <h4 className="text-sm font-semibold text-foreground-900 font-heading mb-3">Dados Gerais</h4>
                <div className="grid grid-cols-2 gap-x-4 gap-y-3 text-sm">
                  <div>
                    <span className="text-foreground-500 text-xs block">Tipo</span>
                    <span className="text-foreground-900">{isUC ? item.categoria : item.tipo}</span>
                  </div>
                  {isUC && (
                    <div>
                      <span className="text-foreground-500 text-xs block">Grupo</span>
                      <span className="text-foreground-900">{item.grupo}</span>
                    </div>
                  )}
                  <div>
                    <span className="text-foreground-500 text-xs block">Município/UF</span>
                    <span className="text-foreground-900">{item.municipio}/{item.uf}</span>
                  </div>
                  {!isUC && (
                    <div>
                      <span className="text-foreground-500 text-xs block">Bairro</span>
                      <span className="text-foreground-900">{item.bairro}</span>
                    </div>
                  )}
                  <div>
                    <span className="text-foreground-500 text-xs block">{isUC ? "Órgão Gestor" : "Responsável"}</span>
                    <span className="text-foreground-900">{isUC ? item.orgaoGestor : item.responsavel}</span>
                  </div>
                  {isUC && (
                    <div>
                      <span className="text-foreground-500 text-xs block">Instrumento Legal</span>
                      <span className="text-foreground-900">{item.instrumentoLegal}</span>
                    </div>
                  )}
                  {isUC && (
                    <div>
                      <span className="text-foreground-500 text-xs block">Plano de Manejo</span>
                      <span className={`badge ${item.temPlanoManejo ? "bg-primary-50 text-primary-700" : "bg-warning-50 text-warning-700"}`}>
                        {item.temPlanoManejo ? "Sim" : "Não"}
                      </span>
                    </div>
                  )}
                  <div>
                    <span className="text-foreground-500 text-xs block">{isUC ? "Status de Gestão" : "Status"}</span>
                    <EnvironmentalStatusBadge status={isUC ? item.statusGestao : item.status} />
                  </div>
                </div>
              </div>

              {!isUC && item.ultimaVistoria && (
                <div className="bg-white rounded-xl border border-background-200 p-4">
                  <h4 className="text-sm font-semibold text-foreground-900 font-heading mb-2">Última Vistoria</h4>
                  <p className="text-sm text-foreground-700">{new Date(item.ultimaVistoria + "T00:00:00").toLocaleDateString("pt-BR", { dateStyle: "long" })}</p>
                </div>
              )}
            </div>
          )}

          {/* Árvores do CTA */}
          {activeTab === "trees" && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-background-200">
                    <th className="text-left py-2 px-2 text-xs font-medium text-foreground-500 uppercase">Código CTA</th>
                    <th className="text-left py-2 px-2 text-xs font-medium text-foreground-500 uppercase">Espécie</th>
                    <th className="text-left py-2 px-2 text-xs font-medium text-foreground-500 uppercase">Estado Fito</th>
                    <th className="text-left py-2 px-2 text-xs font-medium text-foreground-500 uppercase">Risco</th>
                    <th className="text-left py-2 px-2 text-xs font-medium text-foreground-500 uppercase">DAP</th>
                    <th className="text-left py-2 px-2 text-xs font-medium text-foreground-500 uppercase">Recomendação</th>
                    <th className="text-left py-2 px-2 text-xs font-medium text-foreground-500 uppercase">Última Vistoria</th>
                  </tr>
                </thead>
                <tbody>
                  {arvoresAreaVerde.map((arv) => (
                    <tr key={arv.codigoCTA} className="border-b border-background-100 hover:bg-background-50/50">
                      <td className="py-2 px-2"><span className="text-xs font-mono text-accent-700">{arv.codigoCTA}</span></td>
                      <td className="py-2 px-2">
                        <span className="text-xs font-medium text-foreground-900">{arv.especie}</span>
                        <span className="text-[10px] text-foreground-500 block italic">{arv.nomeCientifico}</span>
                      </td>
                      <td className="py-2 px-2">
                        <span className={`badge text-[10px] ${arv.estadoFitossanitario === "Bom" ? "bg-primary-50 text-primary-700" : arv.estadoFitossanitario === "Regular" ? "bg-caution-50 text-caution-700" : "bg-danger-50 text-danger-700"}`}>
                          {arv.estadoFitossanitario}
                        </span>
                      </td>
                      <td className="py-2 px-2">
                        <span className={`badge text-[10px] ${arv.risco === "Baixo" ? "bg-primary-50 text-primary-700" : arv.risco === "Médio" ? "bg-caution-50 text-caution-700" : arv.risco === "Alto" ? "bg-warning-50 text-warning-700" : "bg-danger-50 text-danger-700"}`}>
                          {arv.risco}
                        </span>
                      </td>
                      <td className="py-2 px-2"><span className="text-xs text-foreground-700">{arv.dap} cm</span></td>
                      <td className="py-2 px-2"><span className="text-xs text-foreground-700 max-w-[140px] truncate block">{arv.recomendacao}</span></td>
                      <td className="py-2 px-2"><span className="text-xs text-foreground-500 whitespace-nowrap">{new Date(arv.ultimaVistoria + "T00:00:00").toLocaleDateString("pt-BR")}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Mapa */}
          {activeTab === "map" && (
            <div className="space-y-3">
              <div className="rounded-xl overflow-hidden border border-background-200 h-64 bg-background-100">
                <iframe
                  title="Mapa da área"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  loading="lazy"
                  allowFullScreen
                  referrerPolicy="no-referrer-when-downgrade"
                  src={`https://www.google.com/maps/embed/v1/place?key=AIzaSyBFw0Qbyq9zTFTd-tUY6dZWTgaQzuU17R8&q=${item.coordenadas.lat},${item.coordenadas.lng}&zoom=15&maptype=satellite`}
                ></iframe>
              </div>
              <div className="flex items-center justify-between text-xs text-foreground-500">
                <span>Lat: {item.coordenadas.lat.toFixed(6)} · Lng: {item.coordenadas.lng.toFixed(6)}</span>
                <button className="btn-outline text-xs px-2 py-1 cursor-pointer">
                  <i className="ri-external-link-line mr-1"></i>Abrir mapa completo
                </button>
              </div>
            </div>
          )}

          {/* Vistorias */}
          {activeTab === "inspections" && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <p className="text-sm text-foreground-500">Vistorias vinculadas a esta área</p>
                <button className="btn-primary text-xs px-3 py-1.5 cursor-pointer">
                  <i className="ri-add-line"></i>Nova Vistoria
                </button>
              </div>
              <div className="text-center py-12 bg-background-50 rounded-xl">
                <div className="w-12 h-12 rounded-xl bg-background-100 flex items-center justify-center mx-auto mb-3">
                  <i className="ri-search-eye-line text-foreground-400 text-xl"></i>
                </div>
                <p className="text-sm text-foreground-500">As vistorias da área serão listadas aqui após integração com o módulo de Vistorias.</p>
              </div>
            </div>
          )}

          {/* Documentos */}
          {activeTab === "documents" && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-background-200">
                    <th className="text-left py-2 px-2 text-xs font-medium text-foreground-500 uppercase">Tipo</th>
                    <th className="text-left py-2 px-2 text-xs font-medium text-foreground-500 uppercase">Número</th>
                    <th className="text-left py-2 px-2 text-xs font-medium text-foreground-500 uppercase">Título</th>
                    <th className="text-left py-2 px-2 text-xs font-medium text-foreground-500 uppercase">Data</th>
                    <th className="text-left py-2 px-2 text-xs font-medium text-foreground-500 uppercase">Emissor</th>
                    <th className="text-left py-2 px-2 text-xs font-medium text-foreground-500 uppercase">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {documentosUC.map((doc) => (
                    <tr key={doc.id} className="border-b border-background-100 hover:bg-background-50/50">
                      <td className="py-2 px-2"><span className="text-xs text-foreground-700 whitespace-nowrap">{doc.tipo}</span></td>
                      <td className="py-2 px-2"><span className="text-xs font-mono text-foreground-500">{doc.numero}</span></td>
                      <td className="py-2 px-2"><span className="text-xs text-foreground-900 max-w-[180px] truncate block">{doc.titulo}</span></td>
                      <td className="py-2 px-2"><span className="text-xs text-foreground-500 whitespace-nowrap">{new Date(doc.data + "T00:00:00").toLocaleDateString("pt-BR")}</span></td>
                      <td className="py-2 px-2"><span className="text-xs text-foreground-500 max-w-[140px] truncate block">{doc.emissor}</span></td>
                      <td className="py-2 px-2"><EnvironmentalStatusBadge status={doc.status} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Carbono */}
          {activeTab === "carbon" && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-primary-50 rounded-xl p-4">
                  <p className="text-xs text-primary-700 mb-1">Carbono Estimado</p>
                  <p className="text-2xl font-bold text-primary-900">{carbono.carbonoEstimado} <span className="text-sm font-normal">t</span></p>
                </div>
                <div className="bg-accent-50 rounded-xl p-4">
                  <p className="text-xs text-accent-700 mb-1">CO₂e Estimado</p>
                  <p className="text-2xl font-bold text-accent-900">{carbono.co2eEstimado} <span className="text-sm font-normal">t</span></p>
                </div>
                <div className="bg-background-50 rounded-xl p-4">
                  <p className="text-xs text-foreground-500 mb-1">Árvores Consideradas</p>
                  <p className="text-2xl font-bold text-foreground-900">{carbono.arvoresConsideradas.toLocaleString("pt-BR")}</p>
                </div>
                <div className="bg-background-50 rounded-xl p-4">
                  <p className="text-xs text-foreground-500 mb-1">Nível de Confiança</p>
                  <p className="text-sm font-semibold text-caution-700">{carbono.nivelConfianca}</p>
                </div>
              </div>
              <div className="bg-white rounded-xl border border-background-200 p-4">
                <h4 className="text-sm font-semibold text-foreground-900 font-heading mb-2">Metodologia</h4>
                <p className="text-xs text-foreground-600 leading-relaxed">{carbono.metodologia}</p>
              </div>
              <p className="text-[11px] text-foreground-400">Última atualização: {new Date(carbono.ultimaAtualizacao + "T00:00:00").toLocaleDateString("pt-BR", { dateStyle: "long" })}</p>
            </div>
          )}

          {/* Histórico */}
          {activeTab === "history" && (
            <div className="space-y-0">
              {historicoUC.map((event, idx) => (
                <div key={event.id} className="flex gap-3 pb-4 relative">
                  {/* Timeline line */}
                  {idx < historicoUC.length - 1 && (
                    <div className="absolute left-[15px] top-8 bottom-0 w-0.5 bg-background-200"></div>
                  )}
                  {/* Dot */}
                  <div className={`w-8 h-8 rounded-full ${event.cor} flex items-center justify-center shrink-0 shadow-sm`}>
                    <i className={`${event.icone} text-white text-xs`}></i>
                  </div>
                  {/* Content */}
                  <div className="flex-1 min-w-0 pt-1">
                    <div className="flex items-center gap-2 mb-0.5">
                      <span className="text-xs text-foreground-500">{new Date(event.data + "T00:00:00").toLocaleDateString("pt-BR")}</span>
                      <span className="badge bg-background-200 text-foreground-600 text-[10px]">{event.tipo}</span>
                    </div>
                    <p className="text-sm text-foreground-900">{event.descricao}</p>
                    <p className="text-xs text-foreground-500 mt-0.5">{event.responsavel}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer actions */}
        <div className="shrink-0 px-6 py-3 border-t border-background-200 flex gap-2">
          <button className="btn-outline text-xs px-3 py-2 cursor-pointer flex-1">
            <i className="ri-file-chart-line mr-1"></i>
            Gerar Relatório
          </button>
          <button className="btn-primary text-xs px-3 py-2 cursor-pointer flex-1">
            <i className="ri-edit-line mr-1"></i>
            Editar Cadastro
          </button>
        </div>
      </div>

      <style>{`
        @keyframes slideInRight {
          from { transform: translateX(100%); }
          to { transform: translateX(0); }
        }
      `}</style>
    </>
  );
}